import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, switchMap, take, timeout } from 'rxjs/operators';

import { ErrorModalComponent } from '@app/shared/components/error-modal/error-modal.component';
import { BrowserStorageService } from '@app/services/browser-storage.service';

/**
 * Cuánto se espera al diccionario antes de rendirse (R9b). Sin él no hay texto que
 * mostrar, y quedarse esperando dejaba la pestaña cortada y sin salida.
 */
export const ESPERA_MAXIMA_DICCIONARIO_MS = 10_000;

/** Las dos claves de copy con las que el back da la sesión por terminada. */
export type SesionTerminadaClave = 'errors.session.invalidated' | 'errors.session.userDisabled';

/**
 * Coordinador de la reacción a una sesión terminada por el back (invalidada en
 * otro lugar, token revocado o cuenta deshabilitada). Feature 33 / SPGRA-551.
 *
 * Por qué existe: cada petición en vuelo que volvía rechazada abría SU aviso, y
 * la pantalla seguía consultando con el token muerto — QA vio hasta cinco avisos
 * apilados en medio minuto. La reacción tiene que ser UNA por sesión terminada:
 * un aviso, corte de las peticiones siguientes y login.
 *
 * Es uno por pestaña (`root`): lo consultan el interceptor funcional que corta
 * (`sesionTerminadaInterceptor`), el que absorbe el rechazo tardío con la sesión ya
 * terminada (`sesionTerminadaRechazoInterceptor`) y el `authInterceptor` que
 * clasifica el rechazo.
 *
 * ⚠️ El estado se deriva de la credencial ACTUAL: la sesión sigue terminada
 * mientras `sessionStorage['access']` sea exactamente la que el back rechazó. Un
 * login nuevo en la pestaña escribe otra y el estado se apaga solo (R7), sin un
 * "reset" que alguien tendría que acordarse de llamar. El valor se guarda
 * CIFRADO tal como está en el storage: nunca se descifra ni se registra (R12).
 */
@Injectable({ providedIn: 'root' })
export class SesionTerminadaService {
  private readonly dialog = inject(MatDialog);
  private readonly transloco = inject(TranslocoService);
  private readonly storage = inject(BrowserStorageService);

  private tokenRechazado: string | null = null;
  /** Cubre el rechazo que llegó sin credencial almacenada (no hay con qué casar). */
  private avisoAbierto = false;

  /** `true` mientras la credencial almacenada sea la que el back rechazó. */
  activa(): boolean {
    // Con credencial rechazada conocida, decide SOLO la credencial actual: otra
    // (o ninguna) apaga el estado aunque el aviso anterior siga en pantalla (R7).
    if (this.tokenRechazado !== null) {
      return this.storage.getSessionStorageItem('access') === this.tokenRechazado;
    }
    return this.avisoAbierto;
  }

  /**
   * Reacción única: la primera llamada marca el estado, abre el aviso y, al
   * aceptarlo, ejecuta `alAceptar` una vez. Las siguientes devuelven `EMPTY`.
   */
  reaccionar(clave: SesionTerminadaClave, alAceptar: () => void): Observable<never> {
    if (this.activa()) {
      return EMPTY;
    }

    // Se marca ANTES de cualquier espera: los rechazos que lleguen mientras se
    // carga el diccionario ya tienen que ver la guarda.
    this.tokenRechazado = this.storage.getSessionStorageItem('access');
    this.avisoAbierto = true;

    // ⚠️ La reacción se suscribe AQUÍ y no en el llamador. Si colgara de la
    // petición que la disparó, un llamador que se des-suscribe (pantalla que se
    // destruye, `switchMap` que cancela) se llevaría el aviso — o el "Aceptar" —
    // con él, y la pestaña quedaría cortada sin aviso y sin salida. La cadena
    // termina sola: `take(1)` en el diccionario y en el cierre.
    this.abrirAviso(clave).subscribe(() => {
      this.avisoAbierto = false;
      alAceptar();
    });

    // La petición rechazada completa sin valor ni error (R4): un error llegaría a
    // los `error:` de las pantallas, y varias abren su propio aviso.
    return EMPTY;
  }

  private abrirAviso(clave: SesionTerminadaClave): Observable<unknown> {
    // `selectTranslate` emite cuando el diccionario activo está cargado (R9): el
    // `translate()` síncrono devolvía la clave literal si aún no lo estaba.
    return this.transloco.selectTranslate<string>(clave).pipe(
      take(1),
      // R9b: si el diccionario no llega nunca (falló `/assets/i18n/*.json`), no hay
      // texto que mostrar y abrir el aviso con la clave tampoco sirve: se sale
      // directo al login, que es lo que habría hecho el "Aceptar".
      timeout({ first: ESPERA_MAXIMA_DICCIONARIO_MS, with: () => of(null) }),
      switchMap((texto) => {
        if (texto === null) {
          return of(undefined);
        }
        return this.dialog
          .open(ErrorModalComponent, {
            width: '400px',
            disableClose: true,
            // Nombre accesible del `mat-dialog-container` (R8).
            ariaLabel: texto,
            data: { message: texto },
          })
          .afterClosed();
      }),
      // Revisión 2: un ERROR (del diccionario o al abrir el aviso) no lo atrapa el
      // `timeout` y dejaba `avisoAbierto` en true — la pestaña cortada, sin salida y con
      // el error sin manejar. Cae a la misma rama que R9b: salir al login.
      catchError(() => of(undefined)),
      take(1),
    );
  }
}
